import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { LocaleCode } from '@/api/models';
import type { GetOperationActivityDetailResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

const STALE_TIME_MS = 30_000;

/**
 * One recorded operation in full — what the detail drawer opens.
 *
 * Keyed on the record id **and the locale**, because the account names on the
 * lines are resolved server-side: switching language is a different answer, not
 * a stale one, so it refetches rather than re-rendering. (The catalog is the
 * opposite, and deliberately — it carries both locales on every row because it
 * shows both at once.)
 *
 * `recordId` doubles as the open/closed signal: `null` closes the drawer and
 * disables the query, so nothing is fetched for a drawer nobody opened.
 */
export const useOperationActivityDetailQuery = (
    recordId: string | null,
    locale: LocaleCode,
    enabled: boolean,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetOperationActivityDetailResponse>>({
        queryKey: OperationsQueryKeys.activityDetail(recordId ?? '', locale),
        queryFn: () =>
            apiClient.operations.getActivityDetail(recordId!, locale),
        enabled: enabled && recordId !== null,
        staleTime: STALE_TIME_MS,
    });
};
